import React from 'react';
import { Grid, Box, Typography, CircularProgress, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DoughnutChart from '../components/DoughnutChart';
import SectorDoughnutChart from '../components/SectorDoughnutChart';

const PortfolioSummary = ({ 
  totalCost = 0, 
  totalValue = 0, 
  dailyChange = 0, 
  bestPerformer, 
  worstPerformer, 
  portfolioData = {}, 
  baseCurrency = 'USD',
  sectorAllocation = null,
  loadingSectors = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatCurrency = (value) => {
    if (typeof value !== 'number') return 'N/A';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: baseCurrency }).format(value);
  };

  const renderPerformer = (performer) => {
    if (!performer) return 'N/A';
    return `${performer.symbol} (${formatCurrency(performer.pnl)})`;
  };

  return (
    <Grid container spacing={2} mt={2}>
      {/* First row: Financial summary info - P&L on left, others in line on right */}
      <Grid container item spacing={2}>
        {/* Total P&L - larger box on left */}
        <Grid item xs={12} sm={4} md={3}>
          <Box sx={{ 
            boxShadow: 10, 
            p: 2, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            height: '100%',
            bgcolor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius
          }}>
            <Typography variant="body1" fontWeight="bold" align="center">
              Total P&L:
            </Typography>
            <Typography 
              variant="h5" 
              align="center"
              color={(totalValue - totalCost) > 0 ? 'green' : (totalValue - totalCost) < 0 ? 'red' : 'gray'}
            >
              {formatCurrency(totalValue - totalCost)}
            </Typography>
          </Box>
        </Grid>

        {/* Right column containing the 4 metrics in a row */}
        <Grid item xs={12} sm={8} md={9} container spacing={2}>
          {/* Total Cost */}
          <Grid item xs={6} sm={3} md={3}>
            <Box sx={{ 
              boxShadow: 10, 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              bgcolor: theme.palette.background.paper
            }}>
              <Typography variant="body2" fontWeight="bold" align="center">
                Total Cost:
              </Typography>
              <Typography variant="body1" align="center">
                {formatCurrency(totalCost)}
              </Typography>
            </Box>
          </Grid>

          {/* Total Value */}
          <Grid item xs={6} sm={3} md={3}>
            <Box sx={{ 
              boxShadow: 10, 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              bgcolor: theme.palette.background.paper
            }}>
              <Typography variant="body2" fontWeight="bold" align="center">
                Total Value:
              </Typography>
              <Typography variant="body1" align="center">
                {formatCurrency(totalValue)}
              </Typography>
            </Box>
          </Grid>

          {/* Best/Worst Performer */}
          <Grid item xs={6} sm={3} md={3}>
            <Box sx={{ 
              boxShadow: 10, 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              bgcolor: theme.palette.background.paper
            }}>
              <Typography variant="body2" fontWeight="bold" align="center">
                {totalValue - totalCost > 0 ? 'Best Performer:' : 'Worst Performer:'}
              </Typography>
              <Typography 
                variant="body2" 
                align="center"
                color={totalValue - totalCost > 0 ? 'green' : 'red'}
              >
                {totalValue - totalCost > 0 ? renderPerformer(bestPerformer) : renderPerformer(worstPerformer)}
              </Typography>
            </Box>
          </Grid>
          
          {/* Daily Change */}
          <Grid item xs={6} sm={3} md={3}>
            <Box sx={{ 
              boxShadow: 10, 
              p: 2, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              bgcolor: theme.palette.background.paper
            }}>
              <Typography variant="body2" fontWeight="bold" align="center">
                Daily Change:
              </Typography>
              <Typography 
                variant="body2" 
                align="center"
                color={dailyChange > 0 ? 'green' : dailyChange < 0 ? 'red' : 'gray'}
              >
                {formatCurrency(dailyChange)}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Grid>

      {/* Second row: Charts side by side */}
      <Grid container item spacing={2} mt={1}>
        {/* Asset Allocation Chart */}
        <Grid item xs={isMobile ? 12 : 6}>
          <Box sx={{
            boxShadow: 10,
            p: 2,
            height: '300px',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.paper
          }}>
            <Typography variant="h6" gutterBottom>Allocation ({baseCurrency})</Typography>
            {Object.keys(portfolioData || {}).length > 0 ? (
              <Box sx={{ height: '250px', position: 'relative' }}>
                <DoughnutChart
                  data={portfolioData}
                  headline=""
                  currency={baseCurrency}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography>No data available</Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Sector Allocation Chart */}
        <Grid item xs={isMobile ? 12 : 6}>
          <Box sx={{
            boxShadow: 10,
            p: 2,
            height: '300px',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.background.paper
          }}>
            <Typography variant="h6" gutterBottom>Sector Allocation</Typography>
            {loadingSectors ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : sectorAllocation && Object.keys(sectorAllocation).length > 0 ? (
              <Box sx={{ height: '250px', position: 'relative' }}>
                <SectorDoughnutChart
                  sectorData={sectorAllocation}
                  title=""
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography>No sector data available</Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PortfolioSummary;